const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'knowledge_auth_token';
const USER_KEY = 'knowledge_auth_user';
const listeners = new Set();

function apiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function getStoredUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function storeSession(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  const session = buildSession(token, user);
  listeners.forEach(listener => listener('SIGNED_IN', session));
  return session;
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  listeners.forEach(listener => listener('SIGNED_OUT', null));
}

function buildSession(token, user) {
  if (!token || !user) return null;
  return { access_token: token, user };
}

async function request(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(apiUrl(path), { ...options, headers });
  let payload = null;
  try { payload = await response.json(); } catch { payload = null; }

  if (!response.ok) {
    const message = payload?.error || `Request failed with status ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return payload;
}

class QueryBuilder {
  constructor(table) {
    this.body = { table, action: 'select', filters: [] };
  }

  select(columns = '*', options = {}) {
    this.body.action = 'select';
    this.body.select = columns;
    if (options.count) this.body.count = options.count;
    if (options.head) this.body.head = true;
    return this;
  }

  update(values) {
    this.body.action = 'update';
    this.body.values = values;
    return this;
  }

  insert(values) {
    this.body.action = 'insert';
    this.body.values = values;
    return this;
  }

  delete() {
    this.body.action = 'delete';
    return this;
  }

  eq(column, value) { return this.filter(column, 'eq', value); }
  gte(column, value) { return this.filter(column, 'gte', value); }
  lte(column, value) { return this.filter(column, 'lte', value); }
  gt(column, value) { return this.filter(column, 'gt', value); }
  lt(column, value) { return this.filter(column, 'lt', value); }
  in(column, value) { return this.filter(column, 'in', value); }

  filter(column, op, value) {
    this.body.filters.push({ column, op, value });
    return this;
  }

  order(column, options = {}) {
    this.body.order = { column, ascending: options.ascending !== false };
    return this;
  }

  limit(value) {
    this.body.limit = value;
    return this;
  }

  async execute() {
    try {
      const payload = await request('/api/db/query', {
        method: 'POST',
        body: JSON.stringify(this.body),
      });
      return { data: payload.data ?? null, count: payload.count ?? null, error: null };
    } catch (error) {
      if (error.status === 401) clearSession();
      return { data: null, count: null, error };
    }
  }

  then(resolve, reject) {
    return this.execute().then(resolve, reject);
  }
}

const apiClient = {
  auth: {
    async signInWithPassword({ email, password }) {
      try {
        const payload = await request('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });
        const session = storeSession(payload.token, payload.user);
        return { data: { session, user: payload.user }, error: null };
      } catch (error) {
        return { data: { session: null, user: null }, error };
      }
    },

    async getSession() {
      const token = getToken();
      if (!token) return { data: { session: null }, error: null };
      try {
        const payload = await request('/api/auth/session');
        const session = storeSession(token, payload.user);
        return { data: { session }, error: null };
      } catch (error) {
        clearSession();
        return { data: { session: null }, error };
      }
    },

    async signOut() {
      clearSession();
      return { error: null };
    },

    onAuthStateChange(callback) {
      listeners.add(callback);
      setTimeout(() => callback('INITIAL_SESSION', buildSession(getToken(), getStoredUser())), 0);
      return { data: { subscription: { unsubscribe: () => listeners.delete(callback) } } };
    },
  },

  from(table) {
    return new QueryBuilder(table);
  },

  channel() {
    return { on() { return this; }, subscribe() { return this; } };
  },

  removeChannel() {},
};

export default apiClient;
